/**
 * Inline chat image → H3 video (app.nz via /api/minimax-h3-video).
 * Play button on chat images; does not put image captions into the prompt.
 */
(function (root) {
  'use strict';

  var DEFAULT_MOTION_PROMPT =
    'Subtle natural cinematic motion, the scene gently comes to life with ambient movement';
  var POLL_MS = 2500;

  function authHeaders(includeJson) {
    var headers = {};
    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }
    var creds =
      typeof root.getAuthCredentials === 'function'
        ? root.getAuthCredentials()
        : null;
    var secret =
      (creds && creds.secret) ||
      (root.userData && (root.userData.secret || root.userData.api_key)) ||
      '';
    if (secret && secret !== 'anonymous') {
      headers['X-API-Key'] = secret;
      headers['Authorization'] = 'Bearer ' + secret;
    }
    return headers;
  }

  function notify(message, type) {
    if (root.NetwrckTools && typeof root.NetwrckTools.showNotification === 'function') {
      root.NetwrckTools.showNotification(message, type || 'info');
      return;
    }
    if (typeof root.showSnackbar === 'function') {
      root.showSnackbar(message);
      return;
    }
    if (type === 'error') {
      root.alert(message);
    }
  }

  function ensureLoggedIn() {
    var secret =
      (root.userData && (root.userData.secret || root.userData.api_key)) || '';
    if (secret && secret !== 'anonymous') {
      return true;
    }
    if (typeof root.showSubscribeDialog === 'function') {
      root.showSubscribeDialog();
    } else if (typeof root.showLoginDialog === 'function') {
      root.showLoginDialog();
    } else {
      notify('Sign in to animate images to video', 'error');
    }
    return false;
  }

  function wrapChatImage(img, options) {
    options = options || {};
    if (!img || !img.getAttribute) {
      return null;
    }
    if (img.closest && img.closest('.chat-i2v')) {
      return img.closest('.chat-i2v');
    }
    var src = img.getAttribute('src') || '';
    if (!src || src.indexOf('data:') === 0) {
      return null;
    }

    var wrap = document.createElement('figure');
    wrap.className = 'chat-i2v';
    wrap.dataset.imageUrl = src;

    var media = document.createElement('div');
    media.className = 'chat-i2v__media';

    img.classList.add('chat-i2v__image');
    if (!img.style.maxWidth) {
      img.style.maxWidth = '100%';
    }
    if (!img.style.borderRadius) {
      img.style.borderRadius = '8px';
    }

    var play = document.createElement('button');
    play.type = 'button';
    play.className = 'chat-i2v__play';
    play.setAttribute('aria-label', 'Animate to video');
    play.title = 'Animate to video';
    play.innerHTML = '<i class="material-icons" aria-hidden="true">play_arrow</i>';

    var parent = img.parentNode;
    if (parent) {
      parent.insertBefore(wrap, img);
    }
    media.appendChild(img);
    media.appendChild(play);
    wrap.appendChild(media);

    var status = document.createElement('div');
    status.className = 'chat-i2v__status';
    status.hidden = true;
    wrap.appendChild(status);

    var videoSlot = document.createElement('div');
    videoSlot.className = 'chat-i2v__video-slot';
    wrap.appendChild(videoSlot);

    play.addEventListener('click', function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      animateFigure(wrap, options);
    });

    return wrap;
  }

  function setStatus(wrap, text, isError) {
    var status = wrap.querySelector('.chat-i2v__status');
    if (!status) {
      return;
    }
    if (!text) {
      status.hidden = true;
      status.textContent = '';
      status.classList.remove('chat-i2v__status--error', 'chat-i2v__status--loading');
      return;
    }
    status.hidden = false;
    status.textContent = text;
    status.classList.toggle('chat-i2v__status--error', !!isError);
    status.classList.toggle('chat-i2v__status--loading', !isError);
  }

  function showVideo(wrap, videoUrl) {
    var slot = wrap.querySelector('.chat-i2v__video-slot');
    var play = wrap.querySelector('.chat-i2v__play');
    if (!slot) {
      return;
    }
    slot.innerHTML = '';
    var video = document.createElement('video');
    video.className = 'chat-i2v__video';
    video.src = videoUrl;
    video.controls = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    slot.appendChild(video);

    var actions = document.createElement('div');
    actions.className = 'chat-i2v__actions';
    var download = document.createElement('a');
    download.className = 'chat-i2v__download';
    download.href = videoUrl;
    download.download = '';
    download.target = '_blank';
    download.rel = 'noopener';
    download.textContent = 'Download video';
    actions.appendChild(download);
    slot.appendChild(actions);

    if (play) {
      play.hidden = true;
    }
    wrap.classList.add('chat-i2v--ready');
  }

  function pollStatus(wrap, statusUrl) {
    return fetch(statusUrl, {
      credentials: 'same-origin',
      headers: authHeaders(false),
    })
      .then(function (response) {
        return response.json().then(function (body) {
          return { ok: response.ok, body: body || {} };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(result.body.error || 'Could not read video status');
        }
        var body = result.body;
        var status = String(body.status || '').toLowerCase();
        if (status === 'completed') {
          var videoUrl =
            body.video_url ||
            (body.result && (body.result.video_url || (body.result.video && body.result.video.url)));
          if (!videoUrl) {
            throw new Error('Job completed without a video URL');
          }
          showVideo(wrap, videoUrl);
          var credits = body.credits_charged;
          setStatus(
            wrap,
            credits != null
              ? 'Video ready · ' + credits + ' credits'
              : 'Video ready'
          );
          wrap.dataset.busy = '';
          return;
        }
        if (status === 'failed') {
          throw new Error(body.error || 'Video generation failed');
        }
        setStatus(wrap, 'Generating video…');
        return new Promise(function (resolve) {
          setTimeout(resolve, POLL_MS);
        }).then(function () {
          return pollStatus(wrap, statusUrl);
        });
      });
  }

  function animateFigure(wrap, options) {
    options = options || {};
    if (!wrap || wrap.dataset.busy === '1') {
      return;
    }
    if (!ensureLoggedIn()) {
      return;
    }
    var imageUrl =
      wrap.dataset.imageUrl ||
      (wrap.querySelector('.chat-i2v__image') &&
        wrap.querySelector('.chat-i2v__image').getAttribute('src')) ||
      '';
    if (!imageUrl) {
      notify('Missing image URL', 'error');
      return;
    }

    wrap.dataset.busy = '1';
    wrap.classList.add('chat-i2v--loading');
    setStatus(wrap, 'Starting video…');

    // Intentionally ignore image alt/caption — motion-only prompt.
    var payload = {
      prompt: options.prompt || DEFAULT_MOTION_PROMPT,
      image_url: imageUrl,
      first_frame: imageUrl,
      aspect_ratio: options.aspect_ratio || '1:1',
      size: options.size || 'balanced',
      duration: options.duration || 5,
      steps: options.steps || 20,
      structured_prompt: true,
      include_audio: options.include_audio !== false,
      output_codec: options.output_codec || 'webm-av1',
    };

    fetch('/api/minimax-h3-video', {
      method: 'POST',
      credentials: 'same-origin',
      headers: authHeaders(true),
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        return response.json().then(function (body) {
          return { ok: response.ok, status: response.status, body: body || {} };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var message = result.body.error || 'Could not start video generation';
          if (result.status === 401 || result.status === 402) {
            if (typeof root.showSubscribeDialog === 'function') {
              root.showSubscribeDialog();
            }
          }
          throw new Error(message);
        }
        var statusUrl = result.body.status_url;
        if (!statusUrl) {
          throw new Error('No status URL returned');
        }
        setStatus(wrap, 'Generating video…');
        return pollStatus(wrap, statusUrl);
      })
      .catch(function (error) {
        wrap.dataset.busy = '';
        wrap.classList.remove('chat-i2v--loading');
        setStatus(wrap, error.message || String(error), true);
        notify(error.message || String(error), 'error');
      });
  }

  function appendChatArtImage(imageUrl, container) {
    var host = container;
    if (!host) {
      host =
        (root.jQuery && root.jQuery('.chat-log')[0]) ||
        document.querySelector('.chat-log');
    }
    if (!host || !imageUrl) {
      return null;
    }
    var img = document.createElement('img');
    img.src = imageUrl;
    img.alt = 'Generated art';
    img.loading = 'lazy';
    img.style.cssText =
      'max-width: 100%; min-width: 150px; border-radius: 8px; margin-top: 10px;';
    host.appendChild(img);
    return wrapChatImage(img);
  }

  function enhanceChatAttachments(rootEl) {
    var scope = rootEl || document;
    var images = scope.querySelectorAll(
      '.chat-attachment--image img, .chat-message-attachments img, .chat-log > img:not(.chat-log-avatar-icon)'
    );
    for (var i = 0; i < images.length; i++) {
      wrapChatImage(images[i]);
    }
  }

  root.ChatImageToVideo = {
    wrapChatImage: wrapChatImage,
    appendChatArtImage: appendChatArtImage,
    enhanceChatAttachments: enhanceChatAttachments,
    animateFigure: animateFigure,
    DEFAULT_MOTION_PROMPT: DEFAULT_MOTION_PROMPT,
  };

  function onPlayClick(evt) {
    var button = evt.target && evt.target.closest
      ? evt.target.closest('.chat-i2v__play')
      : null;
    if (!button) {
      return;
    }
    var wrap = button.closest('.chat-i2v');
    if (!wrap) {
      return;
    }
    evt.preventDefault();
    evt.stopPropagation();
    animateFigure(wrap);
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('click', onPlayClick, true);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        enhanceChatAttachments(document);
      });
    } else {
      enhanceChatAttachments(document);
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
